import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Truck, ClipboardList, Gavel, Users, FileText, Bell,
  CreditCard, LogOut, ShieldCheck, Activity, Search, FolderArchive, Menu, X, Download, Navigation, MapPinned, Container, Inbox, Send,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { ROLE_LABEL } from "../lib/format";
import { COMPANY_LOGO, COMPANY_NAME, PRODUCT_NAME } from "../lib/brand";
import api from "../lib/api";

const NavItem = ({ to, icon: Icon, label, testid, end, onClick, disabled }) => {
  if (disabled) {
    return (
      <div
        className="sidebar-link opacity-45 cursor-not-allowed"
        data-testid={testid}
        aria-disabled="true"
        title="Activate your plan to unlock this feature"
      >
        <Icon size={18} strokeWidth={1.5} />
        <span>{label}</span>
      </div>
    );
  }
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
      data-testid={testid}
    >
      <Icon size={18} strokeWidth={1.5} />
      <span>{label}</span>
    </NavLink>
  );
};

const maybeDisabled = (locked, allowed = false) => locked && !allowed;

function NavLinks({ role, isAdmin, unread, locked, onNavigate }) {
  return (
    <>
      <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-bold px-3 mb-2">Operations</div>
      <NavItem to="/app" end icon={LayoutDashboard} label="Dashboard" testid="nav-dashboard" onClick={onNavigate} />
      <NavItem to="/app/loads" icon={ClipboardList} label="My Loads" testid="nav-loads" onClick={onNavigate} disabled={maybeDisabled(locked)} />
      {(role === "carrier_owner" || role === "dispatcher" || isAdmin) && (
        <NavItem to="/app/board" icon={Search} label="Load Board" testid="nav-board" onClick={onNavigate} disabled={maybeDisabled(locked)} />
      )}
      {(role === "shipper" || isAdmin) && (
        <NavItem to="/app/loads/new" icon={Truck} label="Post Load" testid="nav-post-load" onClick={onNavigate} disabled={maybeDisabled(locked)} />
      )}
      {(role === "shipper" || isAdmin) && (
        <NavItem to="/app/quotes" icon={Container} label="Container Quotes" testid="nav-quotes" onClick={onNavigate} disabled={maybeDisabled(locked)} />
      )}
      {role === "shipper" && (
        <NavItem to="/intake" icon={Send} label="Request a Load" testid="nav-intake" onClick={onNavigate} disabled={maybeDisabled(locked)} />
      )}
      {role === "driver" && (
        <NavItem to="/app/matches" icon={Navigation} label="Loads Near Me" testid="nav-matches" onClick={onNavigate} disabled={maybeDisabled(locked)} />
      )}
      <NavItem to="/app/bids" icon={Gavel} label="Bids" testid="nav-bids" onClick={onNavigate} disabled={maybeDisabled(locked)} />
      <NavItem to="/app/documents" icon={FileText} label="Documents" testid="nav-documents" onClick={onNavigate} disabled={maybeDisabled(locked)} />
      <NavItem to="/app/tracking" icon={MapPinned} label="Live Map" testid="nav-tracking" onClick={onNavigate} disabled={maybeDisabled(locked)} />

      <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-bold px-3 mt-6 mb-2">Account</div>
      <NavItem to="/app/billing" icon={CreditCard} label="Billing" testid="nav-billing" onClick={onNavigate} />
      <NavItem to="/app/notifications" icon={Bell} label={`Notifications${unread ? ` (${unread})` : ""}`} testid="nav-notifications" onClick={onNavigate} disabled={maybeDisabled(locked)} />

      {isAdmin && (
        <>
          <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-bold px-3 mt-6 mb-2">Admin</div>
          <NavItem to="/app/admin/intake" icon={Inbox} label="Intake Queue" testid="nav-admin-intake" onClick={onNavigate} disabled={maybeDisabled(locked)} />
          <NavItem to="/app/admin/users" icon={Users} label="Users" testid="nav-admin-users" onClick={onNavigate} disabled={maybeDisabled(locked)} />
          <NavItem to="/app/admin/fee-rules" icon={ShieldCheck} label="Fee Rules" testid="nav-admin-fee-rules" onClick={onNavigate} disabled={maybeDisabled(locked)} />
          {role === "super_admin" && (
            <NavItem to="/app/admin/broker-forms" icon={FolderArchive} label="Broker Forms" testid="nav-admin-broker-forms" onClick={onNavigate} disabled={maybeDisabled(locked)} />
          )}
          <NavItem to="/app/admin/logs" icon={Activity} label="System Logs" testid="nav-admin-logs" onClick={onNavigate} disabled={maybeDisabled(locked)} />
        </>
      )}
    </>
  );
}

const BottomTab = ({ to, icon: Icon, label, testid, end, disabled }) => {
  const base = "flex flex-col items-center justify-center gap-0.5 flex-1 py-2 text-[10px] font-semibold transition-colors";
  if (disabled) {
    return (
      <div className={`${base} text-slate-300 cursor-not-allowed`} data-testid={testid} aria-disabled="true">
        <Icon size={20} strokeWidth={1.75} />
        <span>{label}</span>
      </div>
    );
  }
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `${base} ${isActive ? "text-gold" : "text-slate-400"}`
      }
      data-testid={testid}
    >
      <Icon size={20} strokeWidth={1.75} />
      <span>{label}</span>
    </NavLink>
  );
};

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [billingAccess, setBillingAccess] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [installEvt, setInstallEvt] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/billing/me").then(r => {
      setBillingAccess(r.data.billing_access?.has_access !== false);
    }).catch(() => setBillingAccess(true));
  }, [user]);

  useEffect(() => {
    if (!user || billingAccess) return;
    if (location.pathname !== "/app" && location.pathname !== "/app/billing") {
      navigate("/app", { replace: true });
    }
  }, [billingAccess, location.pathname, navigate, user]);

  useEffect(() => {
    if (!user || !billingAccess) return;
    api.get("/notifications").then(r => {
      setUnread(r.data.filter(n => !n.read).length);
    }).catch(() => {});
  }, [billingAccess, user]);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const promptInstall = async () => {
    if (!installEvt) return;
    installEvt.prompt();
    await installEvt.userChoice;
    setInstallEvt(null);
  };

  if (!user) return null;
  const role = user.role;
  const isAdmin = role === "super_admin" || role === "ops_admin";
  const isCarrier = role === "carrier_owner" || role === "dispatcher";
  const isLocked = !billingAccess;
  const centerTab = isLocked
    ? { to: "/app/billing", icon: CreditCard, label: "Billing", testid: "tab-billing" }
    : role === "driver"
    ? { to: "/app/matches", icon: Navigation, label: "Matches", testid: "tab-matches" }
    : isCarrier
    ? { to: "/app/board", icon: Search, label: "Board", testid: "tab-board" }
    : { to: "/app/loads/new", icon: Truck, label: "Post", testid: "tab-post" };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="w-64 bg-navy flex-shrink-0 hidden md:flex flex-col border-r border-navy-line">
        <div className="h-20 flex items-center px-5 border-b border-navy-line gap-3">
          <div className="w-12 h-12 rounded-md bg-white flex-shrink-0 overflow-hidden flex items-center justify-center">
            <img src={COMPANY_LOGO} alt={COMPANY_NAME} className="w-full h-full object-contain" data-testid="sidebar-logo" />
          </div>
          <div className="min-w-0">
            <div className="font-display text-white text-base font-bold tracking-tight leading-tight">{COMPANY_NAME}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-gold font-bold">on {PRODUCT_NAME}</div>
          </div>
        </div>

        <div className="px-3 py-4 space-y-1 flex-1 overflow-y-auto">
          <NavLinks role={role} isAdmin={isAdmin} unread={unread} locked={isLocked} />
          {installEvt && (
            <button onClick={promptInstall} className="sidebar-link w-full mt-4 text-gold" data-testid="install-app-btn">
              <Download size={18} /> <span>Install app</span>
            </button>
          )}
        </div>

        <div className="p-3 border-t border-navy-line">
          <div className="flex items-center gap-3 px-2 py-2">
            <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full bg-slate-700" />
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-semibold truncate">{user.name}</div>
              <div className="text-slate-400 text-xs">{ROLE_LABEL[user.role]}</div>
            </div>
          </div>
          <button onClick={logout} className="sidebar-link w-full mt-1" data-testid="logout-btn">
            <LogOut size={16} /> <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 md:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawerOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-navy flex flex-col animate-[slideIn_.2s_ease]">
            <div className="h-16 flex items-center justify-between px-4 border-b border-navy-line">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-md bg-white overflow-hidden flex items-center justify-center">
                  <img src={COMPANY_LOGO} alt={COMPANY_NAME} className="w-full h-full object-contain" />
                </div>
                <span className="text-white font-display font-bold text-sm">{COMPANY_NAME}</span>
              </div>
              <button onClick={() => setDrawerOpen(false)} className="text-slate-300 p-1" data-testid="drawer-close"><X size={20} /></button>
            </div>
            <div className="px-3 py-4 space-y-1 flex-1 overflow-y-auto">
              <NavLinks role={role} isAdmin={isAdmin} unread={unread} locked={isLocked} onNavigate={() => setDrawerOpen(false)} />
            </div>
            <div className="p-3 border-t border-navy-line">
              <div className="flex items-center gap-3 px-2 py-2">
                <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full bg-slate-700" />
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-semibold truncate">{user.name}</div>
                  <div className="text-slate-400 text-xs">{ROLE_LABEL[user.role]}</div>
                </div>
              </div>
              {installEvt && (
                <button onClick={promptInstall} className="sidebar-link w-full mt-1 text-gold" data-testid="install-app-btn-mobile">
                  <Download size={16} /> <span>Install app</span>
                </button>
              )}
              <button onClick={logout} className="sidebar-link w-full mt-1" data-testid="logout-btn-mobile">
                <LogOut size={16} /> <span>Sign out</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 overflow-y-auto">
        {/* Mobile top bar */}
        <header className="md:hidden h-14 bg-navy flex items-center px-3 sticky top-0 z-20 gap-2">
          <button onClick={() => setDrawerOpen(true)} className="text-white p-2" data-testid="mobile-menu-btn"><Menu size={22} /></button>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-8 h-8 rounded bg-white overflow-hidden flex items-center justify-center flex-shrink-0">
              <img src={COMPANY_LOGO} alt={COMPANY_NAME} className="w-full h-full object-contain" />
            </div>
            <span className="text-white font-display font-bold text-sm truncate">{COMPANY_NAME}</span>
          </div>
          <button onClick={() => navigate("/app/notifications")} className="relative text-white p-2" data-testid="mobile-notifications">
            <Bell size={20} />
            {unread > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-gold" />}
          </button>
        </header>

        {/* Desktop header */}
        <header className="hidden md:flex h-16 bg-white/80 backdrop-blur-xl border-b border-slate-200 items-center px-6 sticky top-0 z-10">
          <div className="flex-1">
            <div className="text-[11px] tracking-[0.2em] uppercase text-slate-400 font-bold">Logged in as</div>
            <div className="text-sm font-semibold text-slate-900">{user.email} · <span className="text-gold">{ROLE_LABEL[user.role]}</span></div>
          </div>
          <button onClick={() => navigate("/app/notifications")} className="relative p-2 rounded-md hover:bg-slate-100" data-testid="header-notifications">
            <Bell size={18} />
            {unread > 0 && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-gold" />}
          </button>
        </header>

        <div className="p-4 sm:p-6 md:p-8 pb-24 md:pb-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom tab bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-200 flex items-stretch pb-[env(safe-area-inset-bottom)]" data-testid="mobile-bottom-nav">
        <BottomTab to="/app" end icon={LayoutDashboard} label="Home" testid="tab-dashboard" />
        <BottomTab to="/app/loads" icon={ClipboardList} label="Loads" testid="tab-loads" disabled={isLocked} />
        <BottomTab to={centerTab.to} icon={centerTab.icon} label={centerTab.label} testid={centerTab.testid} />
        <BottomTab to="/app/bids" icon={Gavel} label="Bids" testid="tab-bids" disabled={isLocked} />
        <button onClick={() => setDrawerOpen(true)} className="flex flex-col items-center justify-center gap-0.5 flex-1 py-2 text-[10px] font-semibold text-slate-400" data-testid="tab-menu">
          <Menu size={20} strokeWidth={1.75} />
          <span>Menu</span>
        </button>
      </nav>
    </div>
  );
}
